<template>
  <div class="text-xs-center">
    <v-dialog v-model="shown"
      max-width="700"
      persistent
    >
      <v-card>
        <v-card-title class="headline grey lighten-2"
          primary-title
        >
          {{ title }}
        </v-card-title>

        <v-card-text>

          <v-form ref="form">
            <v-container grid-list-md>
              <v-layout wrap>
                <v-flex xs12>
                  <v-text-field v-model="fields.name"
                    label="Name"
                    :rules="rules.name"
                    required></v-text-field>
                </v-flex>
                <v-flex xs12>
                  <v-toolbar class="entitlements-toolbar" color="white" flat>
                    <v-toolbar-title class="subheading font-weight-bold" :class="{ 'red--text': isEntitlementFieldValidationShown }">
                      Entitlements
                      <p v-if="isEntitlementFieldValidationShown" class="ma-0 caption">Please {{ isTree ? 'select' : 'provide' }} at least one entitlement</p>
                    </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-toolbar-items class="hidden-sm-and-down" v-if="isTree">
                      <v-btn class="d-block"
                        @click="toggleTree"
                        icon
                        text
                      >
                        <v-icon>{{ toggleTreeIcon }}</v-icon>
                      </v-btn>
                    </v-toolbar-items>
                  </v-toolbar>

                  <v-treeview ref="tree"
                    v-if="isTree"
                    v-model="entitlementsTreeValue"
                    :items="entitlementsTree"
                    :open="open"
                    active-class=""
                    selected-color="success darken-2"
                    item-children="actions"
                    on-icon="check_box"
                    off-icon="check_box_outline_blank"
                    indeterminate-icon="indeterminate_check_box"
                    open-on-click
                    transition
                    selectable
                  >
                    <template v-slot:label="{ item, leaf }">
                      <span v-if="leaf" class="grey--text text--darken-3 font-weight-bold">{{ item.name }}</span>
                      <strong class="grey--text text--darken-3 py-1 pl-2 pr-3" v-else>{{ item.name }}</strong>
                    </template>
                  </v-treeview>
                  <template v-else>
                    <json-textarea label="JSON Schema"
                      :value.sync="editableSchema"
                      hide-details
                      style="border: 1px solid #eee" />
                  </template>
                </v-flex>

              </v-layout>
            </v-container>
          </v-form>
        </v-card-text>

        <v-divider v-if="showEntitlementsChips && Object.keys(entitlementsValueParsed).length !== 0"></v-divider>

        <div v-if="showEntitlementsChips && Object.keys(entitlementsValueParsed).length !== 0"
          class="px-3 py-2 grey lighten-3"
        >
          <entitlement-chips v-for="(actions, entitlement) in entitlementsValueParsed"
            :key="`${entitlement}`"
            :entitlement="entitlement"
            :actions="typeof actions === 'string' ? actions.split(',') : actions"
          />
        </div>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey"
            :disabled="loading"
            @click="shown = false"
            flat
          >
            Cancel
          </v-btn>
          <v-btn color="success darken-2"
            :disabled="loading"
            :loading="loading"
            @click="save"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { Bus } from '@/bus'
import union from 'lodash.union'
import { Rules } from '@/helpers'

export default {
  name: 'RoleDialog',
  components: {
    EntitlementChips: () => import('../components/EntitlementChips'),
    JsonTextarea: () => import('@/components/JsonEditor/JsonTextarea')
  },
  props: {
    explicitWildcard: {
      type: Boolean,
      required: false,
      default: false
    },
    showEntitlementsChips: {
      type: Boolean,
      required: false,
      default: false
    },
    actionsSeparator: {
      type: String,
      required: false,
      default: ','
    },
    view: {
      type: String,
      required: false,
      default: ''
    }
  },
  data () {
    return {
      shown: false,
      loading: false,
      fields: {},
      entitlementsTreeValue: [],
      open: [],
      role: {},
      showTreeValidation: false,
      editableSchema: '{}'
    }
  },
  computed: {
    /**
     * Entitlements field type is in treeview mode
     */
    isTree () {
      return this.view === 'tree'
    },
    /**
     * Determine if dialog openned in create mode
     */
    isCreate () {
      return !(this.role && this.role.id)
    },
    /**
     * Dialog title
     */
    title () {
      return this.isCreate
        ? 'Create Role'
        : 'Update Role'
    },
    /**
     * Entitlements tree
     */
    entitlementsTree () {
      const entitlements = [ { id: '*', name: '*', actions: this.$store.getters['entitlements/entitlements'] } ]
      return !this.explicitWildcard
        ? entitlements
        : entitlements.map(this.explicitMapper)
    },
    /**
     * Selected entitlements converted into json object for api
     */
    entitlementsValueParsed () {
      if (this.isTree) {
        return this.entitlementsTreeValue.indexOf('*') === -1
          ? this.entitlementsTreeValue.reduce((json, treeItem) => {
            const [ entitlement, action ] = treeItem.split('-')
            if (action && action !== '*' && json[entitlement] !== '*') {
              json[entitlement] = json[entitlement] || ''
              json[entitlement] += `${json[entitlement] === '' ? '' : this.actionsSeparator}${action}`
            } else if (action === undefined) {
              json[entitlement] = '*'
            }
            return json
          }, {})
          : { '*': '*' }
      }

      const entitlements = this.isSchemaValid
        ? JSON.parse(this.editableSchema)
        : {}

      return Object.keys(entitlements).reduce((json, entitlement) => {
        const actions = (typeof entitlements[entitlement] === 'string' ? entitlements[entitlement].split(',') : entitlements[entitlement])

        json[entitlement] = actions.indexOf('*') === -1
          ? actions.filter(action => !!action).join(',')
          : json[entitlement] = '*'

        return json
      }, {})
    },
    rules () {
      return {
        name: [
          ...Rules.admin.name
        ]
      }
    },
    /**
     * Flatten entitlements tree
     */
    flattenTree () {
      return this.entitlementsTree.reduce(this.flatten, [])
    },
    /**
     * Role entitlements converted to flatten tree
     */
    proviededEntitlementsParsed () {
      if (!('entitlements' in this.role)) {
        return []
      }
      const entitlements = Object.keys(this.role.entitlements)

      const indexOfWildcard = entitlements.indexOf('*')

      if (indexOfWildcard !== -1 && this.role.entitlements[entitlements[indexOfWildcard]].indexOf('*') !== 1) {
        return this.flattenTree
      }

      return entitlements.reduce((acc, entitlement) => {
        if (this.role.entitlements[entitlement].indexOf('*') !== -1) {
          acc = union(acc, this.flattenTree.filter(item => item.split('-')[0] === entitlement))
        } else {
          acc = union(acc, this.role.entitlements[entitlement].map(action => `${entitlement}-${action}`))
        }
        return acc
      }, [])
    },
    /**
     * Toggle treeview icon
     */
    toggleTreeIcon () {
      return this.open.length === this.flattenTree.filter(node => node.split('-').length === 1).length
        ? 'expand_less'
        : this.open.length === 0
          ? 'chevron_right'
          : 'expand_more'
    },
    isEntitlementFieldValidationShown () {
      return this.showTreeValidation
        ? Object.keys(this.entitlementsValueParsed).length === 0
        : false
    }
  },
  watch: {
    /**
     * Watch entitlements value for explicit selection
     */
    entitlementsTreeValue (newValue, oldValue) {
      if (newValue.length - oldValue.length === 0 || !this.explicitWildcard) {
        return
      }

      const isAddition = newValue.length > oldValue.length

      const toggled = isAddition
        ? newValue.filter(search => oldValue.indexOf(search) === -1)
        : oldValue.filter(search => newValue.indexOf(search) === -1)

      const entitlement = (toggled.find(t => t.split('-')[0] !== '*') || '').split('-')[0]
      const isGlobalWildcard = toggled.findIndex(t => t === '*-*') !== -1
      const isLocalWildcard = toggled.findIndex(t => t !== '*-*' && t.split('-')[1] === '*') !== -1

      if (isGlobalWildcard && isAddition) {
        this.entitlementsTreeValue = this.flattenTree
      } else if (isLocalWildcard && isAddition) {
        this.entitlementsTreeValue = union(this.entitlementsTreeValue, this.flattenTree.filter(item => item.split('-')[0] === entitlement))
      } else if (!isAddition) {
        this.entitlementsTreeValue = this.entitlementsTreeValue.filter(item => item !== `${entitlement}-*` && item !== '*-*')
      }
    }
  },
  methods: {
    /**
     * Launch role dialog
     */
    launch (role) {
      this.$set(this, 'role', role)
      if (this.isCreate) {
        this.$set(this, 'fields', { name: null, entitlements: {} })
        this.$set(this, 'entitlementsTreeValue', [])
        if ('form' in this.$refs) {
          this.$refs['form'].resetValidation()
        }
        this.showTreeValidation = false
        this.editableSchema = '{}'
      } else {
        this.$set(this, 'fields', { name: this.role.name, entitlements: Object.assign({}, this.role.entitlements) })
        this.$set(this, 'entitlementsTreeValue', this.proviededEntitlementsParsed)
        this.editableSchema = JSON.stringify(role.entitlements)
      }
      this.$set(this, 'shown', true)
    },
    /**
     * Flatten tree
     */
    flatten (tree, item) {
      tree.push(item.id)

      if ('actions' in item) {
        tree.push(...item.actions.reduce(this.flatten, []))
      }

      return tree
    },
    /**
     * Explicit entitlements mapper
     *
     * @param entitlement
     */
    explicitMapper (entitlement) {
      return 'actions' in entitlement
        ? {
          ...entitlement,
          actions: [
            { id: `${entitlement.id}-*`, name: '*' },
            ...entitlement.actions.map(this.explicitMapper)
          ]
        }
        : entitlement
    },
    /**
     * Toggle tree open status
     *
     * Open Entitlements, Hide Actions => Open Entitlements, and Actions => Close Entitlements, and Actions
     */
    toggleTree () {
      this.open = this.open.length === this.flattenTree.filter(node => node.split('-').length === 1).length
        ? []
        : this.open.length === 0
          ? ['*']
          : [ '*', ...this.entitlementsTree.filter(entitlement => entitlement.split('-')[1] === '*') ]
    },
    /**
     * Save role form
     */
    save () {
      if (!(this.$refs['form'].validate() && Object.keys(this.entitlementsValueParsed).length !== 0)) {
        this.showTreeValidation = true
        return
      }

      this.$set(this, 'loading', true)

      const action = this.isCreate ? 'createRole' : 'updateRole'

      let payload = { name: this.fields.name, entitlements: this.entitlementsValueParsed }

      if (!this.isCreate) {
        payload['id'] = this.role.id
      }

      this.$store.dispatch(`entitlements/${action}`, payload)
        .then(() => {
          Bus.$emit('alert', { color: 'success', message: `'${payload.name}' role ${this.isCreate ? 'created' : 'updated'}` })
        })
        .catch(({ message }) => {
          Bus.$emit('alert', ({ color: 'error', message }))
        })
        .finally(() => {
          this.$set(this, 'role', {})
          this.$set(this, 'loading', false)
          this.$set(this, 'shown', false)
        })
    }
  }
}
</script>

<style lang="sass">
// Display role actions in one line
.v-treeview-node--leaf
  display: inline-block
.v-treeview .v-treeview-node__children .v-treeview-node:not(.v-treeview-node--leaf)
  padding: 6px
  margin-top: 6px
  margin-bottom: 6px
.entitlements-toolbar .v-toolbar__content
  padding: 0
.v-treeview > .v-treeview-node > .v-treeview-node__children > .v-treeview-node--leaf
    margin-left: 56px
</style>
